import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DeathConfirmationAttentionUser,
  DeathConfirmationListResponse,
  DeathConfirmationState,
} from '../api/types'
import { CodeChip } from '../components/CodeChip'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { StatusBadge } from '../components/StatusBadge'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

const STATE_FILTERS: DeathConfirmationState[] = [
  'ESCALATING', 'GRACE_PERIOD', 'FINAL_WARNING', 'DELIVERING',
]

const ATTENTION_REASON_FILTERS = [
  { key: 'NO_VERIFIED_EMAIL', label: 'NO_VERIFIED_EMAIL' },
] as const

function deadlineSeconds(row: DeathConfirmationAttentionUser): number {
  const candidates = [
    row.final_warning_deadline,
    row.current_check_deadline,
    row.next_life_check_time,
  ].filter((t) => t > 0)
  if (candidates.length === 0) return 0
  return Math.min(...candidates)
}

interface FilterState {
  state: string
  attentionReason: string
}

function readFilters(params: URLSearchParams): FilterState {
  return {
    state: params.get('state') ?? '',
    attentionReason: params.get('attention_reason') ?? '',
  }
}

export function DeathConfirmationListPage() {
  const client = useAdminApiClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => readFilters(searchParams), [searchParams])

  // Filters are forwarded to the backend so paginated queues remain
  // accurate when matching users live on later pages. The cache key
  // includes the filter state so swapping filters re-fetches from
  // page 1. (review_comments M1, HEAD 47a2b09)
  const result = useCursorPaginatedQuery<DeathConfirmationAttentionUser>({
    queryKey: queryKeys.deathConfirmations({
      state: filters.state,
      attentionReason: filters.attentionReason,
    }),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: '50' })
      if (cursor) search.set('cursor', cursor)
      if (filters.state) search.set('state', filters.state)
      if (filters.attentionReason) {
        search.set('attention_reason', filters.attentionReason)
      }
      return client.get<DeathConfirmationListResponse>(
        `/r/admin/death-confirmations?${search.toString()}`,
      )
    },
  })

  const visibleRows = result.flatItems

  function setFilter(key: keyof FilterState, value: string) {
    const next = new URLSearchParams(searchParams)
    const param = key === 'attentionReason' ? 'attention_reason' : 'state'
    if (value) {
      next.set(param, value)
    } else {
      next.delete(param)
    }
    setSearchParams(next, { replace: true })
  }

  const columns: ColumnConfig<DeathConfirmationAttentionUser>[] = [
    {
      key: 'link_account',
      header: 'Link account',
      cell: (row) => <CodeChip>{row.link_account}</CodeChip>,
    },
    {
      key: 'state',
      header: 'State',
      cell: (row) => (
        <StatusBadge
          kind="death_confirmation"
          value={row.death_confirmation_state}
        />
      ),
    },
    {
      key: 'attention_reason',
      header: 'Attention',
      cell: (row) => row.attention_reason
        ? <span className="text-xs font-medium text-amber-800">
            {row.attention_reason}
          </span>
        : <span className="text-xs text-stone-400">—</span>,
    },
    {
      key: 'deadline',
      header: 'Next deadline',
      cell: (row) => {
        const dl = deadlineSeconds(row)
        return dl
          ? <RelativeTime timestamp={dl} />
          : <span className="text-xs text-stone-400">—</span>
      },
    },
    {
      key: 'activated',
      header: 'Activated msgs',
      align: 'right',
      cell: (row) => row.activated_message_count,
    },
    {
      key: 'verified',
      header: 'Verified emails',
      align: 'right',
      cell: (row) => row.verified_notification_email_count,
    },
  ]

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-stone-900">
          Death-confirmation queue
        </h1>
        <span className="text-xs text-stone-500">
          Read-only — manual reset arrives in Wave 2.
        </span>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-stone-500">
          State:
        </span>
        <FilterChip
          label="Any"
          active={filters.state === ''}
          onClick={() => setFilter('state', '')}
        />
        {STATE_FILTERS.map((s) => (
          <FilterChip
            key={s}
            label={s}
            active={filters.state === s}
            onClick={() => setFilter('state', s)}
          />
        ))}
        <span className="ml-4 text-xs uppercase tracking-wide text-stone-500">
          Attention:
        </span>
        <FilterChip
          label="Any"
          active={filters.attentionReason === ''}
          onClick={() => setFilter('attentionReason', '')}
        />
        {ATTENTION_REASON_FILTERS.map((reason) => (
          <FilterChip
            key={reason.key}
            label={reason.label}
            active={filters.attentionReason === reason.key}
            onClick={() => setFilter('attentionReason', reason.key)}
          />
        ))}
      </div>

      <DataTable<DeathConfirmationAttentionUser>
        caption="Death-confirmation users requiring operator attention"
        columns={columns}
        rows={visibleRows}
        rowKey={(row) => row.link_account}
        isLoading={result.isLoading}
        isError={result.isError}
        errorMessage={result.error?.message ?? 'Failed to load queue.'}
        emptyTitle="No users need attention right now"
        emptyDescription="The death-confirmation daemon will surface users here as they escalate."
        onRowClick={(row) => navigate(`/death-confirmation/${row.link_account}`)}
        footer={
          <PaginationControl
            loadedCount={visibleRows.length}
            hasMore={result.hasMore}
            isFetchingMore={result.isFetchingMore}
            onLoadMore={() => result.loadMore()}
          />
        }
      />
    </div>
  )
}

interface FilterChipProps {
  label: string
  active: boolean
  onClick: () => void
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-full px-2.5 py-0.5 text-xs font-medium ring-1',
        active
          ? 'bg-accent text-white ring-accent'
          : 'bg-white text-stone-700 ring-stone-200 hover:bg-stone-50',
      ].join(' ')}
    >
      {label}
    </button>
  )
}
